import { useEffect, useRef } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import ReviewSection from './ReviewSection';
gsap.registerPlugin(ScrollTrigger);

const bars = [
  { label:'5★', w:88, bg:'#00B67A', pct:'88%' },
  { label:'4★', w:9,  bg:'#4ADE80', pct:'9%'  },
  { label:'3★', w:2,  bg:'#FCD34D', pct:'2%'  },
  { label:'2★', w:1,  bg:'#FB923C', pct:'1%'  },
  { label:'1★', w:0,  bg:'#EF4444', pct:'0%'  },
];

const reviews = [
  { initials:'JM', bg:'linear-gradient(135deg,#00D68F,#00916A)', name:'James Mitchell', loc:'USA', quote:'"My experience with KodersPedia has been extraordinary. Their team were able to bring my vision to life and it simply knocks me out! I am completely amazed by the final result."', tags:['Website Development','Custom Design'], stars:'★★★★★', score:'5.0' },
  { initials:'SR', bg:'linear-gradient(135deg,#34D399,#059669)', name:'Sandra Robertson', loc:'USA', quote:'"The website is taking shape nicely, thanks to the hard work and diligence of the KodersPedia team. Very pleasant company to deal with — would certainly recommend them to others."', tags:['Web Design','Development'], stars:'★★★★★', score:'5.0' },
  { initials:'MH', bg:'linear-gradient(135deg,#60A5FA,#2563EB)', name:'Michael Harrison', loc:'USA', quote:'"I would recommend KodersPedia to anyone! They are super professional and responsive, always going above and beyond, making sure I am 100% satisfied. Absolutely the BEST!"', tags:['Professional','Responsive'], stars:'★★★★★', score:'5.0' },
  { initials:'LA', bg:'linear-gradient(135deg,#A78BFA,#7C3AED)', name:'Linda Anderson', loc:'USA', quote:'"The team was incredibly patient and worked with me despite my own delays. They redesigned my website and added new features which has significantly helped me improve my business."', tags:['Website Redesign','Patient Team'], stars:'★★★★★', score:'5.0' },
  { initials:'DT', bg:'linear-gradient(135deg,#FB923C,#EA580C)', name:'David Thompson', loc:'USA', quote:'"I want to thank the entire KodersPedia team for being so helpful, patient and going beyond what was initially foreseen. I am very happy with the App we have created together."', tags:['App Development','Above & Beyond'], stars:'★★★★★', score:'5.0' },
  { initials:'RC', bg:'linear-gradient(135deg,#F472B6,#DB2777)', name:'Rachel Collins', loc:'USA', quote:'"KodersPedia brought a great design. The team was very professional, flexible and successful at overcoming challenges. Thanks to their hard work our website looks absolutely great!"', tags:['Design','Professional','Flexible'], stars:'★★★★★', score:'5.0' },
];

export default function TrustpilotSection() {
  const secRef = useRef(null);

  useEffect(() => {
    const sec = secRef.current;
    if (!sec) return;
    ScrollTrigger.create({
      trigger: sec, start: 'top 70%',
      onEnter: () => {
        sec.querySelectorAll('.tp-bar-fill').forEach(el => {
          el.style.width = el.dataset.w + '%';
        });
      }
    });
  }, []);

  return (
    <ReviewSection id="trust" dark>
      <div ref={secRef}>
        <div className="rs-label-row reveal-fade">
          <span className="rs-num">04</span>
          <span className="rs-platform-label">[ TRUSTPILOT REVIEWS ]</span>
          <span className="rs-score">4.0 / 5.0</span>
        </div>
        <div className="rs-giant-wrap">
          <div className="rs-giant reveal-clip" aria-hidden="true">TRUSTPILOT</div>
          <div className="rs-giant-meta reveal-fade">
            <div className="rs-stars-big" style={{color:'#00B67A'}}>★★★★☆</div>
            <div className="rs-count">
              43+ Real Reviews &nbsp;·&nbsp;{' '}
              <a href="https://www.trustpilot.com/review/koderspedia.com" target="_blank" rel="noopener noreferrer" style={{color:'#00B67A',fontSize:12,textDecoration:'none',borderBottom:'1px solid rgba(0,182,122,.4)'}}>View All on Trustpilot ↗</a>
            </div>
          </div>
          <h2 className="rs-heading reveal-fade">KodersPedia Reviews on Trustpilot</h2>
          <p className="rs-subheading reveal-fade">Read verified feedback from clients who worked with KodersPedia</p>
        </div>

        <h3 className="rs-h3 reveal-fade">Trustpilot Customer Rating Breakdown</h3>
        <div className="tp-score-block reveal-up">
          <div className="tp-big-num">4.0</div>
          <div className="tp-right">
            <div className="tp-badge">43+ REAL REVIEWS</div>
            <div className="tp-bars">
              {bars.map(b => (
                <div key={b.label} className="tp-bar-row">
                  <span>{b.label}</span>
                  <div className="tp-bar-track">
                    <div className="tp-bar-fill" data-w={b.w} style={{width:0, background:b.bg}} />
                  </div>
                  <span>{b.pct}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="rs-list">
          {reviews.map((r, i) => (
            <div key={i} className="rs-item reveal-up">
              <div className="rs-item-left">
                <div className="rs-av" style={{background:r.bg}}>{r.initials}</div>
                <div>
                  <div className="rs-name">{r.name}</div>
                  <div className="rs-role">{r.loc} &nbsp;<em className="tp-verified">✓ Verified</em></div>
                </div>
              </div>
              <div className="rs-item-center">
                <div className="rs-quote">{r.quote}</div>
                <div className="rs-tags">{r.tags.map(t => <span key={t} className="rs-tag">{t}</span>)}</div>
              </div>
              <div className="rs-item-right">
                <span className="rs-stars-sm" style={{color:'#00B67A'}}>{r.stars}</span>
                <span className="rs-score-sm">{r.score}</span>
              </div>
            </div>
          ))}
        </div>

        <div style={{maxWidth:1300,margin:'40px auto 0',textAlign:'center'}}>
          <a href="https://www.trustpilot.com/review/koderspedia.com" target="_blank" rel="noopener noreferrer" style={{display:'inline-flex',alignItems:'center',gap:8,background:'rgba(0,182,122,.1)',border:'1px solid rgba(0,182,122,.3)',color:'#00B67A',fontSize:13,fontWeight:600,padding:'12px 28px',borderRadius:100,textDecoration:'none',letterSpacing:'.03em'}}>
            <svg width="16" height="16" viewBox="0 0 24 24"><circle cx="12" cy="12" r="11" fill="#00B67A"/><polygon points="12,6 13.5,10.5 18,10.5 14.5,13 15.5,17.5 12,15 8.5,17.5 9.5,13 6,10.5 10.5,10.5" fill="white"/></svg>
            Read All 43+ Reviews on Trustpilot →
          </a>
        </div>
      </div>
    </ReviewSection>
  );
}
